import { useQuery } from "react-query";

import axios from "axios";
import { AUTOMATION_SERVICE_ADDRESS } from "constants/app.constants";

export interface AutomationJobStatus {
  status: "waiting" | "active" | "completed" | "failed" | "delayed" | "not_found";
  progress: number;
  result?: {
    success: boolean;
    filePaths?: string[];
    zipPath?: string;
    completedAt?: string;
    error?: string;
  };
  createdAt: string;
  processedAt?: string;
  finishedAt?: string;
}

export default function useAutomationJobStatus(
  jobId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    onSuccess?: (data: AutomationJobStatus) => void;
    onError?: (error: Error) => void;
  }
) {
  return useQuery({
    queryKey: ["automation-job-status", jobId],
    queryFn: async (): Promise<AutomationJobStatus> => {
      if (!jobId) throw new Error("No job ID provided");

      const response = await axios.get(
        `${AUTOMATION_SERVICE_ADDRESS}/automation/job/${jobId}/status`,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      if (response.status !== 200) {
        throw new Error(`Failed to get job status: ${response.statusText}`);
      }

      return response.data as AutomationJobStatus;
    },
    enabled: !!jobId && (options?.enabled ?? true),
    refetchInterval: (data) => {
      // Stop polling if job is completed or failed
      if (data?.status === "completed" || data?.status === "failed") {
        return false;
      }
      return 5000; //5 seconds
    },
    refetchIntervalInBackground: true,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    retry: (failureCount, error) => {
      // Don't retry if job not found
      if (error.message.includes("404") || error.message.includes("not_found")) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    }
  });
}
